import React, { ComponentProps } from "react";
import classNames from "classnames";
import Link from "next/link";

import Chip from "components/botanic/Chip";
import Tag from "components/botanic/Tag";
import Image from "components/Image";
import { Sizes } from "components/Image/Image";

const PLACEHOLDER_IMAGE =
  "https://leafly-public.imgix.net/web-home/article-placeholder.jpg";

type Props = {
  /**
   * The name of the article's author
   */
  author?: string;
  className?: string;
  /**
   * The date the article was published; does not have to be formatted
   */
  date?: string;
  /**
   * Disable image lazyloading; used for above the fold images
   */
  disableLazyLoad?: boolean;
  /**
   * The article excerpt
   */
  excerpt?: string;
  /**
   * Toggle to display featured variant of card
   */
  featured?: boolean;
  /**
   * URL the news card should link to
   */
  href: string;
  /**
   * URL for the cover image
   */
  imageUrl?: string;
  onClick?: () => void;
  /**
   * Image sizes array for use in image component. Default is [285]
   */
  sizes?: Sizes;
  /**
   * An array of tags that should be displayed on the article.
   * A maximum of two (2) tags will be displayed.
   */
  tags?: string[];
  /**
   * The article's title
   */
  title: string;

  imageProps?: Omit<ComponentProps<typeof Image>, "alt" | "ratio" | "src">;
};

const getUrlPath = (href: string) => {
  try {
    const { pathname } = new URL(href);
    return pathname;
  } catch (e) {
    return href;
  }
};

const ArticleCard = ({
  author,
  className,
  date,
  excerpt,
  featured,
  href,
  onClick,
  tags,
  title,
  imageUrl,
  imageProps,
  ...others
}: Props) => {
  const classes = classNames(
    "block h-full elevation-low rounded overflow-hidden",
    { "featured-card": featured },
    className
  );

  const formattedDate =
    date &&
    new Date(date).toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      timeZone: "America/Los_Angeles",
      year: "numeric",
    });

  const formattedHref = href.startsWith("/") ? href : getUrlPath(href);

  return (
    <Link
      href={formattedHref}
      className={classes}
      onClick={onClick}
      {...others}
    >
      <div className="text-left flex flex-col h-full w-full bg-white">
        <div className="relative">
          <Image
            {...imageProps}
            alt={`${title} image`}
            ratio="rectangle"
            sizes={imageProps?.sizes || [285]}
            src={imageUrl || PLACEHOLDER_IMAGE}
          />
          {featured && tags && (
            <div className="absolute bottom-0 left-0 p-lg z-10">
              {tags.slice(0, 2).map((tag, i) => (
                <Chip className="mr-sm" key={i}>
                  {tag}
                </Chip>
              ))}
            </div>
          )}
        </div>
        <div className="p-md h-full flex flex-col justify-between">
          <div>
            {!featured &&
              tags &&
              tags.slice(0, 2).map((tag, i) => (
                <Tag className="mr-sm mb-sm" key={i}>
                  {tag}
                </Tag>
              ))}
            <div
              className={`font-bold ${featured ? "text-md" : "text-sm"}`}
              dangerouslySetInnerHTML={{ __html: title }}
            />
          </div>
          {author || formattedDate ? (
            <div className={featured ? "mt-xs" : "mt-md"}>
              {author && (
                <div
                  className="text-sm"
                  dangerouslySetInnerHTML={{ __html: author }}
                />
              )}
              {formattedDate && <div className="text-xs">{formattedDate}</div>}
            </div>
          ) : null}
          {excerpt && (
            <p className={`mt-lg mb-none ${featured ? "text-sm" : "text-xs"}`}>
              {excerpt}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
};

export default ArticleCard;
